import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { searchTopics, topicsFor, topicsForStructure, type Page, type Topic } from '@syndiqia/manual';
import { PageHeader } from '../components/ui';
import { useI18n } from '../i18n';
import { useSyndicate } from '../session';

const ORDER: Page[] = ['start', 'home', 'profile', 'requests', 'documents', 'units', 'parties', 'quote-parts', 'finances', 'approvals', 'maintenance', 'governance', 'reservations', 'insurance', 'automation', 'onboarding', 'settings'];

/** The user guide: the topics that apply to the person (by permission), the current page's first, with a search. */
export function HelpPage() {
  const { t, locale } = useI18n();
  const h = t.help;
  const s = useSyndicate();
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const from = params.get('page') as Page | null;
  const [query, setQuery] = useState('');
  const mine = useMemo(() => topicsForStructure(topicsFor((p) => s.can(p as never)), s.legalStructure), [s]);
  const found = query.trim().length > 2 ? searchTopics(query, mine, 8) : null;
  const here = from ? mine.filter((x) => x.page === from) : [];
  const groups = ORDER.map((page) => ({ page, topics: mine.filter((x) => x.page === page && !here.includes(x)) })).filter((g) => g.topics.length > 0);

  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash]);

  const topic = (x: Topic) => (
    <article key={x.id} id={x.id} className={`panel help-topic${hash === `#${x.id}` ? ' help-topic-target' : ''}`}>
      <h3>{x.title[locale]}</h3>
      <ol>
        {x.body[locale].map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ol>
    </article>
  );

  return (
    <>
      <PageHeader title={h.title} intro={h.intro} />
      <input type="search" className="help-search" placeholder={h.search} value={query} onChange={(e) => setQuery(e.target.value)} aria-label={h.search} />
      {found ? (
        <section>
          <h2>{h.results}</h2>
          {found.length === 0 ? <p className="muted">{h.noResults}</p> : found.map(topic)}
        </section>
      ) : (
        <>
          {here.length > 0 && from && (
            <section>
              <h2>{h.onThisPage(h.pages[from])}</h2>
              {here.map(topic)}
            </section>
          )}
          {groups.map((g) => (
            <section key={g.page}>
              <h2>{h.pages[g.page]}</h2>
              {g.topics.map(topic)}
            </section>
          ))}
        </>
      )}
      <p className="muted small">
        {h.askHint} <Link to={s.base}>{h.askLink}</Link>
      </p>
    </>
  );
}
